/**
 * Document text-direction handling for right-to-left languages.
 *
 * The settings dialog used to be the only place that set
 * `document.documentElement.dir`, which meant the direction was correct until
 * the next page load and then silently reverted to LTR — i18next restores the
 * language from its own localStorage cache on boot, but nothing restored the
 * direction to match. Arabic therefore rendered with Arabic strings in a
 * left-to-right layout after any refresh.
 *
 * Applying direction from the language itself, on init and on every change,
 * keeps the two in sync no matter which surface changed the language.
 */
/**
 * Languages shipped in `lang/` that are written right-to-left.
 */
const RTL_LANGUAGES = new Set(['ar', 'he', 'fa']);
/**
 * Whether the given language code is written right-to-left.
 *
 * @param {string} language - A language code, possibly region-qualified.
 * @returns {boolean}
 */
export function isRtlLanguage(language) {
    if (!language) {
        return false;
    }
    return RTL_LANGUAGES.has(language.toLowerCase().split(/[-_]/)[0]);
}
/**
 * Set `dir` and `lang` on the document element to match the given language.
 *
 * No-op outside a DOM (native builds import this module through i18next).
 *
 * @param {string} language - The language now in effect.
 * @returns {void}
 */
export function applyDocumentDirection(language) {
    if (typeof document === 'undefined' || !document.documentElement) {
        return;
    }
    const dir = isRtlLanguage(language) ? 'rtl' : 'ltr';
    document.documentElement.dir = dir;
    if (language) {
        document.documentElement.lang = language;
    }
}
